export const OPTION_CODE_TO_LABEL: Record<string, Record<string, string>> = {
  // --- General Medical Options ---
  currentMedicalConditions: {
    none: "None",
    diabetes: "Diabetes",
    hypertension: "Hypertension",
    heart_disease: "Heart Disease",
    asthma: "Asthma",
    arthritis: "Arthritis",
    depression: "Depression/Anxiety",
    thyroid: "Thyroid Disorder",
    other: "Other",
  },
  smokingHabits: {
    no: "I do not smoke",
    yes_1_5: "Yes, 1-5 cigarettes/day",
    yes_5_10: "Yes, 5-10 cigarettes/day",
    yes_over_10: "Yes, over 10/day",
  },
  alcoholUse: {
    no: "No",
    occasionally: "Occasionally",
    regularly: "Regularly",
    daily: "Daily",
  },
  exerciseFrequency: {
    none: "None",
    "1_2_week": "1-2 times/week",
    "3_5_week": "3-5 times/week",
    daily: "Daily",
  },

  // --- Vascular Options ---
  vascularPrimaryGoal: {
    chest_pain: "Chest pain",
    circulation_issues: "Circulation issues (legs, arms)",
    varicose_veins: "Varicose veins",
    high_blood_pressure: "High blood pressure",
    stroke_prevention: "Stroke prevention",
    other: "Other",
  },
  vascularSymptoms: {
    yes_daily: "Yes, daily",
    yes_occasionally: "Yes, occasionally",
    no_symptoms: "No symptoms, preventative evaluation",
  },
  vascularSymptomTypes: {
    pain: "Pain",
    swelling: "Swelling",
    numbness: "Numbness",
    shortness_of_breath: "Shortness of breath",
    fatigue: "Fatigue",
    other: "Other",
  },
  vascularDiagnosisStatus: {
    known: "Yes (diagnosis known)",
    unsure: "Yes (but unsure of details)",
    seeking: "No, seeking evaluation",
  },
  vascularUrgency: {
    immediately: "Immediately",
    "1_2_months": "Within 1–2 months",
    flexible: "Flexible",
  },
  vascularProceduralExpectation: {
    diagnostic: "Diagnostic testing only",
    non_surgical: "Non-surgical treatment",
    surgical: "Surgical intervention",
    consultation: "Doctor recommendation",
  },

  // --- Hair Transplant Options ---
  hairTransplantRelevantFactors: {
    pregnancy_breastfeeding: "Pregnancy or breastfeeding (now or in the past year)",
    postpartum: "Postpartum hair shedding (within the past 12 months)",
    menopause_hormonal: "Perimenopause or menopause-related hair changes",
    pcos_hormonal: "PCOS or other hormone-related hair loss",
    no_factors: "None of these apply",
  },
  hairTransplantTreatmentAreas: {
    temples: "Temples",
    hairline: "Hairline",
    frontal_scalp: "Frontal Scalp",
    midscalp_i: "Mid-scalp I",
    midscalp_ii: "Mid-scalp II",
    crown: "Crown",
    beard: "Beard",
    other: "Other",
  },
  hairTransplantProgression: {
    stable: "Stable",
    slowly_worsening: "Slowly worsening",
    rapidly_worsening: "Rapidly worsening",
  },
  hairTransplantPreviousTreatments: {
    none: "None",
    medications:
      "Medications (e.g. minoxidil; spironolactone or finasteride only when prescribed)",
    prp: "PRP",
    previous_transplant: "Previous transplant",
  },
  hairTransplantOutcomeExpectation: {
    natural_hairline: "Natural-looking hairline",
    part_mid_scalp: "Better density at the part / mid-scalp",
    density: "Overall density",
    coverage: "Coverage",
    minimal_scarring: "Minimal scarring",
  },
  hairTransplantTimeline: {
    asap: "ASAP",
    "1_3_months": "1–3 months",
    flexible: "Flexible",
  },

  // --- Optical Options ---
  opticalPrimaryReason: {
    blurry_vision: "Blurry vision",
    glasses_contacts: "Glasses or contacts",
    lasik_evaluation: "LASIK evaluation",
    cataracts: "Cataracts",
    disease_monitoring: "Eye disease monitoring",
    routine_exam: "Routine exam",
    other: "Other",
  },
  opticalVisionCorrection: {
    glasses: "Glasses",
    contacts: "Contact lenses",
    both: "Both",
    neither: "Neither",
  },
  opticalLaterality: {
    left: "Left",
    right: "Right",
    both: "Both",
  },
  opticalSymptoms: {
    pain: "Eye pain",
    headaches: "Headaches",
    dry_eyes: "Dry eyes",
    light_sensitivity: "Light sensitivity",
    night_vision: "Night vision issues",
    none: "None",
  },
  opticalProcedureInterest: {
    yes: "Yes",
    no: "No",
    unsure: "Unsure / want evaluation",
  },

  // --- Plastic Surgery Options ---
  plasticAreaOfInterest: {
    face: "Face",
    breast: "Breast",
    body: "Body",
    multiple: "Multiple areas",
    other: "Other",
  },
  plasticProcedureType: {
    cosmetic: "Cosmetic enhancement",
    reconstructive: "Reconstructive surgery",
    revision: "Revision of prior surgery",
    other: "Other",
  },
  plasticSpecificConcern: {
    appearance: "Appearance improvement",
    symmetry: "Symmetry",
    function: "Function",
    correction: "Correction of prior work",
    other: "Other",
  },
  plasticPreviousSurgery: {
    yes: "Yes",
    no: "No",
  },
  plasticOutcomePriority: {
    natural: "Natural look",
    minimal_downtime: "Minimal downtime",
    longevity: "Longevity of results",
    cost: "Cost efficiency",
    other: "Other",
  },

  // --- Diagnostics Options ---
  diagnosticsPurpose: {
    doctor_recommendation: "Doctor recommendation",
    ongoing_condition: "Ongoing condition",
    new_symptoms: "New symptoms",
    preventative: "Preventative screening",
    other: "Other",
  },
  diagnosticsType: {
    mri: "MRI",
    ct: "CT",
    xray: "X-ray",
    ultrasound: "Ultrasound",
    pet: "PET",
    unsure: "Not sure",
  },
  diagnosticsArea: {
    head_brain: "Head/brain",
    spine: "Spine",
    chest: "Chest",
    abdomen: "Abdomen",
    pelvis: "Pelvis",
    extremities: "Extremities",
    other: "Other",
  },
  diagnosticsTimeSensitivity: {
    urgent: "Urgent",
    within_weeks: "Within weeks",
    flexible: "Flexible",
  },
  diagnosticsReason: {
    routine_check: "Routine health check",
    ongoing_symptoms: "Ongoing symptoms",
    monitoring: "Monitoring an existing condition",
    doctor_recommendation: "Doctor recommendation",
    preventative: "Preventative / longevity screening",
    pre_procedure: "Pre-procedure clearance",
    other: "Other",
  },
  diagnosticsSymptoms: {
    fatigue: "Fatigue",
    weight_changes: "Weight changes",
    pain: "Pain or inflammation",
    digestive: "Digestive issues",
    hormonal: "Hormonal symptoms",
    dizziness: "Dizziness or headaches",
    sleep: "Sleep issues",
    other: "Other",
  },
  diagnosticsConditions: {
    diabetes: "Diabetes",
    thyroid: "Thyroid condition",
    hormonal_imbalance: "Hormonal imbalance",
    autoimmune: "Autoimmune condition",
    cardiovascular: "Cardiovascular condition",
    anemia: "Anemia or blood disorder",
    none: "None",
    other: "Other",
  },
  diagnosticsDoctorStatus: {
    yes_list: "Yes, I have a list",
    yes_no_list: "Yes, but no specific list",
    no_guidance: "No, I want guidance",
  },
  diagnosticsScope: {
    specific: "Specific tests only",
    comprehensive: "Comprehensive panel",
    unsure: "Unsure – want recommendations",
  },
  diagnosticsLabUrgency: {
    asap: "As soon as possible",
    "1_2_weeks": "Within 1–2 weeks",
    flexible: "Flexible",
  },
  diagnosticsResultsUsage: {
    doctor_review: "Review with my doctor",
    medical_travel: "Use for medical travel planning",
    preventative: "Preventative monitoring",
    personal_tracking: "Personal health tracking",
    unsure: "Not sure yet",
  },
  diagnosticsFollowUp: {
    yes: "Yes",
    no: "No",
    maybe: "Maybe, depending on results",
  },

  // --- Fertility Options ---
  fertilityCareFocus: {
    evaluation: "Fertility evaluation",
    ivf: "IVF",
    egg_freezing: "Egg freezing",
    sperm_analysis: "Sperm analysis",
    hormonal: "Hormonal assessment",
    other: "Other",
  },
  fertilityPatientType: {
    individual: "Individual",
    couple: "Couple",
  },
  fertilityPreviousAttempts: {
    yes: "Yes",
    no: "No",
  },
  fertilityKnownDiagnosis: {
    yes: "Yes",
    no: "No",
    unsure: "Unsure",
  },
  fertilityTimeline: {
    immediately: "Immediately",
    months_3_6: "Within 3–6 months",
    planning: "Planning stage",
  },

  // --- Bariatric Options ---
  bariatricPrimaryGoal: {
    weight_loss: "Weight loss",
    improve_conditions: "Improve obesity-related conditions",
    improve_mobility: "Improve mobility",
    // Legacy keys (older forms / stored data)
    health_improvement: "Health improvement",
    mobility_improvement: "Improved mobility",
    other: "Other",
  },
  bariatricPriorWeightLoss: {
    diet_exercise: "Diet & Exercise",
    medications: "Weight loss medications",
    previous_surgery: "Previous bariatric surgery",
    none: "None",
  },
  bariatricProcedureInterest: {
    gastric_sleeve: "Gastric Sleeve",
    gastric_bypass: "Gastric Bypass",
    gastric_balloon: "Gastric Balloon",
    revision: "Revision Surgery",
    undecided: "Undecided",
    // Legacy: interest yes/no (if ever stored on this field)
    yes: "Yes",
    no: "No",
    unsure: "Unsure / want consultation",
  },
  bariatricCurrentDiet: {
    healthy: "Generally healthy",
    emotional: "Emotional eating",
    snacking: "Frequent snacking",
    large_portions: "Large portions",
    other: "Other",
  },
  bariatricPreviousAttempts: {
    diet_exercise: "Diet & exercise",
    structured_program: "Structured program",
    medications: "Weight-loss medications",
    none: "None",
    other: "Other",
  },
  bariatricTimeline: {
    asap: "ASAP",
    months_3_6: "Within 3–6 months",
    months_6_12: "Within 6–12 months",
    planning: "Planning stage",
    flexible: "Flexible",
    other: "Other",
  },

  // --- Stem Cell Options ---
  stemCellReason: {
    joint_pain: "Joint pain",
    arthritis: "Arthritis",
    sports_injury: "Sports injury",
    neurological: "Neurological condition",
    autoimmune: "Autoimmune condition",
    anti_aging: "Anti-aging / wellness",
    other: "Other",
  },
  stemCellLocation: {
    knee: "Knee",
    shoulder: "Shoulder",
    hip: "Hip",
    spine: "Spine",
    multiple: "Multiple joints",
    whole_body: "Whole-body / systemic",
  },
  stemCellDiagnosisStatus: {
    yes: "Yes",
    no: "No",
    unsure: "Unsure",
  },
  stemCellTreatmentGoal: {
    pain_reduction: "Pain reduction",
    mobility: "Improved mobility",
    degeneration: "Slowed degeneration",
    recovery: "Recovery after injury",
    wellness: "General wellness",
  },
  stemCellPriorTreatments: {
    physical_therapy: "Physical therapy",
    injections: "Injections",
    surgery: "Surgery",
    none: "None",
  },

  // --- Orthopedic Options ---
  orthopedicPrimaryConcern: {
    joint_pain: "Joint pain",
    sports_injury: "Sports injury",
    spine_back: "Spine/Back issues",
    arthritis: "Arthritis",
    other: "Other",
  },
  orthopedicAffectedArea: {
    knee: "Knee",
    hip: "Hip",
    shoulder: "Shoulder",
    spine: "Spine/Back",
    hand_wrist: "Hand/Wrist",
    foot_ankle: "Foot/Ankle",
  },
  orthopedicDuration: {
    recent: "Recent injury (days/weeks)",
    "1_3_months": "1-3 months",
    "3_6_months": "3-6 months",
    over_year: "Over a year",
  },
  orthopedicPriorTreatments: {
    physical_therapy: "Physical therapy",
    injections: "Injections",
    medications: "Medications",
    prior_surgery: "Prior surgery",
    none: "None",
  },

  // --- ENT Options ---
  entPrimaryConcern: {
    hearing_loss: "Hearing loss",
    sinus_issues: "Sinus issues",
    throat_vocal: "Throat/Vocal",
    ear_infection_pain: "Ear infection/pain",
    sleep_apnea: "Sleep apnea",
    other: "Other",
  },
  entSymptoms: {
    nasal_congestion: "Nasal congestion",
    tinnitus: "Ringing in ears (tinnitus)",
    vertigo_dizziness: "Vertigo/Dizziness",
    swallowing_difficulty: "Difficulty swallowing",
    snoring: "Snoring",
    other: "Other",
  },
  entDuration: {
    less_month: "Less than a month",
    "1_6_months": "1-6 months",
    over_6_months: "Over 6 months",
  },
  entPreviousSurgeries: {
    yes: "Yes",
    no: "No",
  },

  // --- Neuro Surgery Options ---
  neuroSurgeryPrimaryConcern: {
    brain_tumor: "Brain tumor",
    spine_disc: "Spine disc / nerve compression",
    aneurysm_avm: "Aneurysm / AVM",
    brain_injury: "Brain or spinal injury",
    movement_disorder: "Movement disorder",
    other: "Other",
  },
  neuroSurgerySymptoms: {
    headache: "Severe headaches",
    weakness_numbness: "Weakness or numbness",
    balance_issues: "Balance or walking issues",
    seizures: "Seizures",
    pain: "Persistent neck/back pain",
    other: "Other",
  },
  neuroSurgeryDiagnosisStatus: {
    confirmed: "Yes, confirmed diagnosis",
    imaging_only: "Imaging done, awaiting diagnosis",
    evaluation_needed: "No, I need evaluation",
  },
  neuroSurgeryPriorTreatment: {
    none: "No prior treatment",
    medications: "Medication management",
    procedure_surgery: "Prior procedure or surgery",
    rehab_therapy: "Rehabilitation / therapy",
  },
  neuroSurgeryTimeline: {
    urgent: "Urgent (as soon as possible)",
    weeks_2_4: "Within 2-4 weeks",
    months_1_2: "Within 1-2 months",
    flexible: "Flexible",
  },

  // --- Urology Options ---
  urologyPrimaryConcern: {
    kidney_stones: "Kidney stones",
    prostate: "Prostate concerns (BPH, PSA, screening)",
    bladder_urinary: "Bladder / urinary symptoms",
    male_fertility_ed: "Male fertility / erectile dysfunction",
    incontinence: "Incontinence",
    kidney_disease: "Kidney disease or function concerns",
    other: "Other",
  },
  urologySymptoms: {
    pain: "Pain or discomfort",
    blood_in_urine: "Blood in urine",
    frequency_urgency: "Frequent urination / urgency",
    difficulty_urinating: "Difficulty urinating or weak stream",
    burning: "Burning with urination",
    leakage: "Leakage / incontinence episodes",
    nocturia: "Waking at night to urinate",
    other: "Other",
  },
  urologyDuration: {
    less_month: "Less than a month",
    "1_6_months": "1-6 months",
    over_6_months: "Over 6 months",
  },
  urologyDiagnosisStatus: {
    confirmed: "Yes, confirmed diagnosis",
    testing_only: "Tests/imaging done, awaiting diagnosis",
    evaluation_needed: "No, I need evaluation",
  },
  urologyPriorTreatment: {
    none: "No prior treatment",
    medications: "Medication management",
    procedure_surgery: "Prior procedure or surgery",
    other_specialist: "Seen another specialist",
  },
  urologyTimeline: {
    urgent: "Urgent (as soon as possible)",
    weeks_2_4: "Within 2-4 weeks",
    months_1_2: "Within 1-2 months",
    flexible: "Flexible",
  },

  // --- Other Specialty Options ---
  otherSpecialtyFocus: {
    consultation: "General specialist consultation",
    second_opinion: "Second medical opinion",
    symptom_evaluation: "Evaluation of symptoms",
    treatment_planning: "Treatment planning",
    other: "Other",
  },
  otherSpecialtyBodyAreas: {
    head_neck: "Head / neck",
    chest: "Chest",
    abdomen: "Abdomen",
    back_spine: "Back / spine",
    arms_legs: "Arms / legs",
    multiple: "Multiple areas",
    none: "Not area-specific",
  },
  otherSpecialtySymptoms: {
    pain: "Pain",
    swelling: "Swelling",
    mobility_limitation: "Mobility limitation",
    fatigue: "Fatigue",
    recent_diagnosis: "Recent diagnosis",
    none: "No active symptoms",
  },
  otherSpecialtyTimeline: {
    asap: "As soon as possible",
    "1_2_weeks": "Within 1-2 weeks",
    "1_2_months": "Within 1-2 months",
    flexible: "Flexible",
  },


  // --- Wellness & Healthcare Options ---
  wellnessPrimaryGoal: {
    // Wellness & Healthcare (general program)
    preventive_care: "Preventive Care",
    holistic_healing: "Holistic Healing",
    anti_aging: "Anti-aging / Longevity",
    health_optimization: "Health Optimization",
    // Therapy-specific primary goal (acupuncture, massage, etc.)
    pain_relief: "Pain relief",
    improved_mobility: "Improved mobility",
    general_wellness: "General wellness",
    other: "Other",
  },
  wellnessCurrentPractices: {
    nutritional_supplements: "Nutritional Supplements",
    meditation_mindfulness: "Meditation / Mindfulness",
    regular_exercise: "Regular Exercise",
    alternative_medicine: "Alternative Medicine",
    none: "None",
  },
  wellnessSpecificConcerns: {
    sleep_issues: "Sleep Issues",
    stress_anxiety: "Stress / Anxiety",
    energy_levels: "Energy Levels",
    nutrition: "Nutrition",
    other: "Other",
  },
  wellnessTimeline: {
    asap: "ASAP",
    months_3_6: "Within 3–6 months",
    planning: "Planning stage",
    flexible: "Flexible",
    other: "Other",
  },
  wellnessPreviousExperience: {
    yes: "Yes",
    no: "No",
    unsure: "Unsure",
  },

  // Acupuncture / Colema
  wellnessAcupunctureConcern: {
    pain_relief: "Pain relief",
    improved_mobility: "Improved mobility",
    general_wellness: "General wellness",
    other: "Other",
  },
  wellnessAcupunctureTargetArea: {
    back: "Back (body)",
    neck: "Neck",
    shoulders: "Shoulders",
    abdomen: "Abdomen",
    other: "Other",
  },
  wellnessAcupunctureFrequency: {
    once_week: "Once a week",
    twice_week: "Twice a week",
    weekly: "Weekly",
    flexible: "Flexible",
    other: "Other",
  },

  // Hyperbaric
  wellnessHyperbaricReason: {
    recovery: "Recovery",
    wound_healing: "Wound healing",
    sports_recovery: "Sports recovery",
    other: "Other",
  },
  wellnessHyperbaricUrgency: {
    immediately: "Immediately",
    within_1_2_months: "Within 1–2 months",
    flexible: "Flexible",
    other: "Other",
  },
  wellnessHyperbaricSessions: {
    "5_10": "5-10 sessions",
    "10_15": "10-15 sessions",
    "20_30": "20-30 sessions",
    "30_plus": "More than 30 sessions",
    other: "Other",
  },

  // Massage
  wellnessMassageFocus: {
    relaxation: "Relaxation",
    pain_relief: "Pain relief",
    mobility_improvement: "Improved mobility",
    other: "Other",
  },
  wellnessMassagePressure: {
    gentle: "Gentle",
    moderate: "Moderate",
    deep: "Deep",
    other: "Other",
  },

  // Reflexology
  wellnessReflexologyTarget: {
    feet: "Feet",
    hands: "Hands",
    whole_body: "Whole body",
    other: "Other",
  },
  wellnessReflexologyMainSymptoms: {
    fatigue: "Fatigue",
    pain: "Pain",
    digestive: "Digestive issues",
    hormonal: "Hormonal symptoms",
    sleep: "Sleep issues",
    other: "Other",
  },

  // Ozone
  wellnessOzoneReason: {
    inflammation: "Inflammation",
    recovery: "Recovery",
    immunity: "Immune support",
    other: "Other",
  },
  wellnessOzonePreviousExperience: {
    yes: "Yes",
    no: "No",
    unsure: "Unsure",
  },

  // Functional doctor
  wellnessFunctionalDoctorProgram: {
    chinese_medicine_acupuncture: "Chinese Medicine (with acupuncture)",
    quantum_healing: "Quantum Healing",
    iv_therapies: "IV Therapies",
    yoga_8am: "Yoga M to F 8AM",
    other: "Other",
  },
  wellnessFunctionalDoctorExperience: {
    yes: "Yes",
    no: "No",
    unsure: "Unsure",
  },
};

/** Locale for resolving option codes to display labels in the UI. */
export type IntakeOptionLocale = "en" | "es";

/**
 * Spanish labels merged over {@link OPTION_CODE_TO_LABEL} when the active UI locale is Spanish.
 * Add keys here as needed for translated review summaries (email/API payloads stay English).
 */
export const OPTION_CODE_TO_LABEL_ES: Partial<
  Record<string, Record<string, string>>
> = {
  otherSpecialtyFocus: {
    consultation: "Consulta general con especialista",
    second_opinion: "Segunda opinión médica",
    symptom_evaluation: "Evaluación de síntomas",
    treatment_planning: "Planificación del tratamiento",
    other: "Otro",
  },
  otherSpecialtyBodyAreas: {
    head_neck: "Cabeza / cuello",
    chest: "Tórax",
    abdomen: "Abdomen",
    back_spine: "Espalda / columna",
    arms_legs: "Brazos / piernas",
    multiple: "Varias zonas",
    none: "No específico de una zona",
  },
  otherSpecialtySymptoms: {
    pain: "Dolor",
    swelling: "Hinchazón",
    mobility_limitation: "Limitación de movilidad",
    fatigue: "Fatiga",
    recent_diagnosis: "Diagnóstico reciente",
    none: "Sin síntomas activos",
  },
  otherSpecialtyTimeline: {
    asap: "Lo antes posible",
    "1_2_weeks": "En 1–2 semanas",
    "1_2_months": "En 1–2 meses",
    flexible: "Flexible",
  },
  hairTransplantRelevantFactors: {
    pregnancy_breastfeeding: "Embarazo o lactancia (ahora o en el último año)",
    postpartum: "Caída de cabello posparto (en los últimos 12 meses)",
    menopause_hormonal: "Cambios en el cabello por perimenopausia o menopausia",
    pcos_hormonal: "SOP u otra pérdida de cabello relacionada con hormonas",
    no_factors: "Ninguna de las anteriores",
  },
  hairTransplantTreatmentAreas: {
    temples: "Sienes",
    hairline: "Línea del cabello",
    frontal_scalp: "Cuero cabelludo frontal",
    midscalp_i: "Cuero cabelludo medio I",
    midscalp_ii: "Cuero cabelludo medio II",
    crown: "Coronilla",
    beard: "Barba",
    other: "Otro",
  },
  hairTransplantPreviousTreatments: {
    medications:
      "Medicamentos (p. ej. minoxidil; espironolactona o finasterida solo si están prescritos)",
  },
  hairTransplantOutcomeExpectation: {
    natural_hairline: "Línea del cabello de aspecto natural",
    part_mid_scalp: "Más densidad en la raya / zona media del cuero cabelludo",
    density: "Densidad general",
    coverage: "Cobertura",
    minimal_scarring: "Cicatrices mínimas",
  },
  neuroSurgeryPrimaryConcern: {
    brain_tumor: "Tumor cerebral",
    spine_disc: "Disco de columna / compresión nerviosa",
    aneurysm_avm: "Aneurisma / MAV",
    brain_injury: "Lesión cerebral o medular",
    movement_disorder: "Trastorno del movimiento",
    other: "Otro",
  },
  neuroSurgerySymptoms: {
    headache: "Dolores de cabeza intensos",
    weakness_numbness: "Debilidad o entumecimiento",
    balance_issues: "Problemas de equilibrio o al caminar",
    seizures: "Convulsiones",
    pain: "Dolor persistente de cuello/espalda",
    other: "Otro",
  },
  neuroSurgeryDiagnosisStatus: {
    confirmed: "Sí, diagnóstico confirmado",
    imaging_only: "Imágenes realizadas, pendiente de diagnóstico",
    evaluation_needed: "No, necesito evaluación",
  },
  neuroSurgeryPriorTreatment: {
    none: "Sin tratamiento previo",
    medications: "Manejo con medicamentos",
    procedure_surgery: "Procedimiento o cirugía previa",
    rehab_therapy: "Rehabilitación / terapia",
  },
  neuroSurgeryTimeline: {
    urgent: "Urgente (lo antes posible)",
    weeks_2_4: "En 2-4 semanas",
    months_1_2: "En 1-2 meses",
    flexible: "Flexible",
  },
  urologyPrimaryConcern: {
    kidney_stones: "Cálculos renales",
    prostate: "Problemas de próstata (HPB, PSA, cribado)",
    bladder_urinary: "Síntomas de vejiga / urinarios",
    male_fertility_ed: "Fertilidad masculina / disfunción eréctil",
    incontinence: "Incontinencia",
    kidney_disease: "Enfermedad renal o problemas de función",
    other: "Otro",
  },
  urologySymptoms: {
    pain: "Dolor o molestia",
    blood_in_urine: "Sangre en la orina",
    frequency_urgency: "Micción frecuente / urgencia",
    difficulty_urinating: "Dificultad para orinar o chorro débil",
    burning: "Ardor al orinar",
    leakage: "Pérdidas / episodios de incontinencia",
    nocturia: "Despertar por la noche para orinar",
    other: "Otro",
  },
  urologyDuration: {
    less_month: "Menos de un mes",
    "1_6_months": "1-6 meses",
    over_6_months: "Más de 6 meses",
  },
  urologyDiagnosisStatus: {
    confirmed: "Sí, diagnóstico confirmado",
    testing_only: "Pruebas/imágenes realizadas, pendiente de diagnóstico",
    evaluation_needed: "No, necesito evaluación",
  },
  urologyPriorTreatment: {
    none: "Sin tratamiento previo",
    medications: "Manejo con medicamentos",
    procedure_surgery: "Procedimiento o cirugía previa",
    other_specialist: "Consulta con otro especialista",
  },
  urologyTimeline: {
    urgent: "Urgente (lo antes posible)",
    weeks_2_4: "En 2-4 semanas",
    months_1_2: "En 1-2 meses",
    flexible: "Flexible",
  },
};

function resolveOptionLabel(
  mapKey: string,
  code: string,
  locale: IntakeOptionLocale,
): string {
  const enLabel = OPTION_CODE_TO_LABEL[mapKey]?.[code];
  if (!enLabel) return code;
  if (locale !== "es") return enLabel;

  const esExplicit = OPTION_CODE_TO_LABEL_ES[mapKey]?.[code];
  return esExplicit ?? enLabel;
}

/** Resolves stored option code(s) to human-readable labels for the given UI locale. */
export function formatOptionSelection(
  mapKey: string,
  raw: string | string[] | undefined,
  locale: IntakeOptionLocale,
): string {
  if (raw === undefined || raw === null) return "";
  const en = OPTION_CODE_TO_LABEL[mapKey];
  if (!en) {
    return Array.isArray(raw) ? raw.join(", ") : String(raw);
  }
  if (Array.isArray(raw)) {
    return raw
      .map((v) => resolveOptionLabel(mapKey, String(v), locale))
      .join(", ");
  }
  return resolveOptionLabel(mapKey, String(raw), locale);
}

/** Reverse maps for hydrating API responses (labels → option codes). */
export function buildLabelToCodeMaps(): Record<string, Record<string, string>> {
  const result: Record<string, Record<string, string>> = {};
  for (const [fieldKey, codeToLabel] of Object.entries(OPTION_CODE_TO_LABEL)) {
    const labelToCode: Record<string, string> = {};
    for (const [code, label] of Object.entries(codeToLabel)) {
      labelToCode[label] = code;
      labelToCode[label.toLowerCase()] = code;
    }
    result[fieldKey] = labelToCode;
  }
  return result;
}
